// Real-time virtual surfer for demo mode: responds to the practice loop
// (lie down, pop up on the cue, hold, reset) and reports landmarks the way the
// pose model would, so the whole app can be tried — and tested — without a
// camera or a taped floor.

import type { Stance } from '../core/mapping';
import { blendBodies, footPoints, pronePose, smoothstep, stancePose, standingAside, type Body } from './body';
import { observeBody, offsetPose, physicalLabel, popupMotion, rng, CLEAN_SCRIPT, type Offset, type PopupScript } from './popup';
import { makeWorld, type SimWorld } from './world';

export interface DemoScenario {
  name: string;
  script: PopupScript;
  /** Feet the camera loses, ms relative to the pop-up start. */
  occlusions: { foot: 'front' | 'rear'; from: number; to: number }[];
}

type Act =
  | { kind: 'away' }
  | { kind: 'aside' }
  | { kind: 'stance'; front: Offset; rear: Offset }
  | { kind: 'prone' }
  | { kind: 'popup'; at: (tau: number) => Body; t0: number; scenario: DemoScenario };

export class LiveSimulator {
  readonly world: SimWorld;
  private act: Act = { kind: 'away' };
  private transition: { from: Body | null; t0: number; dur: number } | null = null;
  private readonly r: ReturnType<typeof rng>;
  private readonly occOffset: { x: number; y: number };
  /** Called when the rider decides to pop up by themselves (self-start mode). */
  onSelfStart: (() => void) | null = null;
  private selfStartAt: number | null = null;
  lastScenario: DemoScenario | null = null;

  constructor(opts: { stance?: Stance; seed?: number } = {}) {
    this.world = makeWorld({ stance: opts.stance ?? 'regular' });
    this.r = rng(opts.seed ?? 42);
    this.occOffset = { x: 0.012, y: -0.01 };
  }

  get state(): Act['kind'] {
    return this.act.kind;
  }

  private go(next: Act, now: number, dur = 1100): void {
    this.transition = { from: this.bodyAt(now), t0: now, dur };
    this.act = next;
  }

  stepAway(now: number): void {
    if (this.act.kind !== 'away') this.go({ kind: 'away' }, now, 900);
  }

  standAside(now: number): void {
    this.go({ kind: 'aside' }, now);
  }

  /** Stand in the target stance, with the small placement scatter a real rider has. */
  standInStance(now: number): void {
    const j = () => this.r.gauss() * 1.2;
    this.go({ kind: 'stance', front: { along: j(), across: j() }, rear: { along: j(), across: j() } }, now, 1300);
  }

  lieDown(now: number, selfStart = false): void {
    this.go({ kind: 'prone' }, now, 1500);
    this.selfStartAt = selfStart ? now + 2500 + this.r.next() * 2500 : null;
  }

  popUp(now: number, scenario: DemoScenario = randomScenario(this.r)): void {
    if (this.act.kind !== 'prone') return;
    const { at } = popupMotion(this.world, scenario.script);
    this.lastScenario = scenario;
    this.selfStartAt = null;
    this.transition = null;
    this.act = { kind: 'popup', at, t0: now, scenario };
  }

  bodyAt(now: number): Body | null {
    if (this.selfStartAt !== null && now >= this.selfStartAt && this.act.kind === 'prone') {
      this.selfStartAt = null;
      this.popUp(now);
      this.onSelfStart?.();
    }
    const target = this.actBody(now);
    const tr = this.transition;
    if (tr && now < tr.t0 + tr.dur) {
      const k = smoothstep((now - tr.t0) / tr.dur);
      const from = tr.from ?? standingAside(this.world, 170 * Math.sign(this.world.camera.pos.x));
      const to = target ?? standingAside(this.world, 170 * Math.sign(this.world.camera.pos.x));
      if (!tr.from && !target) return null;
      // Walk in/out from the side rather than appearing.
      return blendBodies(from, to, k);
    }
    this.transition = null;
    return target;
  }

  private actBody(now: number): Body | null {
    const w = this.world;
    switch (this.act.kind) {
      case 'away':
        return null;
      case 'aside':
        return standingAside(w, 110 * Math.sign(w.camera.pos.x));
      case 'stance': {
        const f = footPoints(offsetPose(w, w.targets.front, this.act.front));
        const r = footPoints(offsetPose(w, w.targets.rear, this.act.rear));
        return stancePose(w, f, r, 1, { x: Math.sin(now / 800) * 1.2, y: Math.cos(now / 1000) * 1.2 });
      }
      case 'prone':
        return pronePose(w, 0.05 + Math.sin(now / 700) * 0.012);
      case 'popup':
        return this.act.at(now - this.act.t0);
    }
  }

  /** Landmarks for this instant (null when nobody is in view or the model misses a frame). */
  landmarks(now: number): Float32Array | null {
    const body = this.bodyAt(now);
    if (!body || this.r.next() < 0.01) return null;
    const occluded: Partial<Record<'L' | 'R', { x: number; y: number }>> = {};
    if (this.act.kind === 'popup') {
      const tau = now - this.act.t0;
      for (const o of this.act.scenario.occlusions) {
        if (tau >= o.from && tau <= o.to) occluded[physicalLabel(this.world, o.foot)] = this.occOffset;
      }
    }
    return observeBody(this.world, body, this.r, { jitterPx: 1.2, occluded, swapLabels: false });
  }
}

// ---------------------------------------------------------------------------

/** A realistic mix of attempts: mostly decent, with the usual mistakes. */
export function randomScenario(r: ReturnType<typeof rng>): DemoScenario {
  const vary = (v: number, spread: number) => v + (r.next() * 2 - 1) * spread;
  const base: PopupScript = {
    ...CLEAN_SCRIPT,
    reactionMs: vary(330, 90),
    pressMs: vary(650, 150),
    swingMs: vary(480, 140),
    frontLand: { along: r.gauss() * 2, across: r.gauss() * 2 },
    rearLand: { along: r.gauss() * 2, across: r.gauss() * 2 },
    adjustments: [],
  };
  const landing = base.reactionMs + base.pressMs + base.swingMs;
  const pick = r.next();
  const sign = () => (r.next() < 0.5 ? -1 : 1);
  if (pick < 0.42) return { name: 'clean', script: base, occlusions: [] };
  if (pick < 0.56) return { name: 'front short', script: { ...base, frontLand: { along: -vary(16, 5), across: base.frontLand.across } }, occlusions: [] };
  if (pick < 0.62) return { name: 'front long', script: { ...base, frontLand: { along: vary(14, 4), across: 0 } }, occlusions: [] };
  if (pick < 0.71) return { name: 'back foot off centre', script: { ...base, rearLand: { along: 0, across: sign() * vary(13, 3) } }, occlusions: [] };
  if (pick < 0.77) return { name: 'narrow', script: { ...base, frontLand: { along: -vary(11, 3), across: 0 }, rearLand: { along: vary(11, 3), across: 0 } }, occlusions: [] };
  if (pick < 0.9) {
    const miss = vary(15, 4);
    return {
      name: 'shuffle',
      script: { ...base, frontLand: { along: -miss, across: 0 }, adjustments: [{ foot: 'front', afterMs: vary(380, 120), along: miss, across: 0 }] },
      occlusions: [],
    };
  }
  if (pick < 0.95) return { name: 'pivot', script: { ...base, adjustments: [{ foot: 'front', afterMs: 400, along: 0, across: 0, rot: 28 }] }, occlusions: [] };
  return { name: 'hidden landing', script: base, occlusions: [{ foot: 'front', from: landing - 250, to: landing + 650 }] };
}

/** The simulated rider following an experiment instruction (validation lab demo). */
export function scenarioFor(key: string, r: ReturnType<typeof rng>): DemoScenario {
  const base = randomScenario({ next: () => 0.1, gauss: r.gauss }).script; // a clean template with natural timing
  const v = (x: number) => x * (0.85 + r.next() * 0.3);
  switch (key) {
    case 'frontShort':
      return { name: key, script: { ...base, frontLand: { along: -v(16), across: 0 } }, occlusions: [] };
    case 'frontLong':
      return { name: key, script: { ...base, frontLand: { along: v(15), across: 0 } }, occlusions: [] };
    case 'rearOff':
      return { name: key, script: { ...base, rearLand: { along: 0, across: v(14) * (r.next() < 0.5 ? -1 : 1) } }, occlusions: [] };
    case 'narrow':
      return { name: key, script: { ...base, frontLand: { along: -v(12), across: 0 }, rearLand: { along: v(12), across: 0 } }, occlusions: [] };
    case 'wide':
      return { name: key, script: { ...base, frontLand: { along: v(12), across: 0 }, rearLand: { along: -v(12), across: 0 } }, occlusions: [] };
    case 'shuffle': {
      const miss = v(15);
      return { name: key, script: { ...base, frontLand: { along: -miss, across: 0 }, adjustments: [{ foot: 'front', afterMs: v(400), along: miss, across: 0 }] }, occlusions: [] };
    }
    default:
      return { name: 'clean', script: base, occlusions: [] };
  }
}

export { makeWorld };
